import * as XLSX from "xlsx";
import { formatearMontoMoneda } from "../Helpers/utilidades-globales";

const ImportFromExcel = ({
  processedData,
  setProcessedData,
  sanitizeData,
  helperText,
}) => {
  const convertExcelDateToJSDate = (excelDate) => {
    // El serial de Excel guarda el día en la parte entera y la hora en la fracción.
    // Tomamos solo la parte entera (Math.floor) y la leemos en UTC: así obtenemos
    // exactamente la fecha calendario del Excel, sin corrimientos por la hora de la
    // celda (p. ej. 23:59) ni por la zona horaria del navegador/servidor.
    const serialDia = Math.floor(excelDate);
    const date = new Date((serialDia - 25569) * 86400 * 1000); // serial -> medianoche UTC del día
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`; // Formato DD/MM/YYYY
  };

  const normalizeDateString = (dateStr) => {
    let str = String(dateStr).replace(/-/g, "/");
    const parts = str.split("/");
    if (parts.length === 3) {
      let [d, m, y] = parts;
      const monthMap = {
        jan: "01",
        ene: "01",
        feb: "02",
        mar: "03",
        apr: "04",
        abr: "04",
        may: "05",
        jun: "06",
        jul: "07",
        aug: "08",
        ago: "08",
        sep: "09",
        sept: "09",
        oct: "10",
        nov: "11",
        dec: "12",
        dic: "12",
      };
      d = String(d).padStart(2, "0");
      const normalizedMonth = monthMap[String(m).trim().toLowerCase()];
      m = normalizedMonth || String(m).padStart(2, "0");
      if (y.length === 2) {
        y = "20" + y;
      }
      return `${d}/${m}/${y}`;
    }
    return dateStr;
  };

  const normalizeHeader = (header) => {
    return header
      .replace(/\./g, "") // Quitar puntos
      .replace(/\s+/g, "_") // Reemplazar espacios por guiones bajos
      .toLowerCase(); // Convertir a minúsculas
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];

    const parseCurrency = (str) => {
      if (typeof str === "number") return str;
      if (!str) return NaN;
      let cleaned = String(str).trim();
      let isNav = cleaned.startsWith("(") && cleaned.endsWith(")");
      if (isNav) cleaned = "-" + cleaned.substring(1, cleaned.length - 1);
      
      // Eliminar símbolos de moneda y espacios
      cleaned = cleaned.replace(/[^0-9.,-]/g, "");

      let lastDot = cleaned.lastIndexOf(".");
      let lastComma = cleaned.lastIndexOf(",");
      
      if (lastDot > -1 && lastComma > -1) {
        if (lastComma > lastDot) {
          cleaned = cleaned.replace(/\./g, "").replace(/,/g, ".");
        } else {
          cleaned = cleaned.replace(/,/g, "");
        }
      } else if (lastComma > -1) {
        let parts = cleaned.split(",");
        if (parts.length > 2 || parts[parts.length - 1].length === 3) {
          cleaned = cleaned.replace(/,/g, "");
        } else {
          cleaned = cleaned.replace(/,/g, ".");
        }
      }
      return Number(cleaned);
    };

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        let unifiedData = []; // Aquí almacenaremos los datos unificados

        workbook.SheetNames.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const rawDataText = XLSX.utils.sheet_to_json(sheet, {
            header: 1, // Leer como array (sin headers definidos)
            defval: "", // Evitar celdas vacías
            raw: false, // Forzar a texto como muestran las celdas de Excel
          });
          const rawDataVal = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: "",
            raw: true, // Forzar valores numéricos reales (para fechas)
          });

          const matrices = [];
          let currentHeaders = null;

          rawDataText.forEach((row, rowIndex) => {
            // Identificar si la fila tiene encabezados
            const nonEmptyCells = row.filter((cell) => cell !== "");
            if (nonEmptyCells.length > 3 && !currentHeaders) {
              // Considerar esta fila como encabezado si tiene suficientes celdas no vacías
              currentHeaders = row.map((header) =>
                normalizeHeader(header || "")
              );
            } else if (currentHeaders) {
              // Si hay encabezados definidos, procesar las filas siguientes como datos
              const rowData = {};
              currentHeaders.forEach((header, index) => {
                if (header) {
                  let cellValue = row[index] || "";
                  // Convertir números de serie de Excel a fechas legibles
                  if (header.toLowerCase().includes("fecha")) {
                    let rawCell = rawDataVal[rowIndex][index];
                    if (typeof rawCell === "number") {
                      cellValue = convertExcelDateToJSDate(rawCell);
                    } else if (typeof cellValue === "string" && cellValue !== "") {
                      cellValue = normalizeDateString(cellValue);
                    }
                  }
                  rowData[header] = cellValue;
                }
              });

              // Solo agregar filas con datos no vacíos
              if (Object.values(rowData).some((value) => value !== "")) {
                if (
                  matrices.length === 0 ||
                  matrices[matrices.length - 1].headers !== currentHeaders
                ) {
                  // Crear una nueva matriz si es la primera o si cambia el conjunto de encabezados
                  matrices.push({
                    headers: currentHeaders,
                    rows: [],
                  });
                }
                matrices[matrices.length - 1].rows.push(rowData);
              }
            }
          });

          // Guardar las matrices procesadas por hoja
          matrices.forEach((matrix) => {
            // Filtrar filas con mayoría de datos vacíos
            let cleanedRows = matrix.rows.filter((row) => {
              const values = Object.values(row);
              const nonEmptyValues = values.filter((value) => value !== "");
              // Mantener si tiene más de la mitad de sus valores no vacíos
              return nonEmptyValues.length > values.length / 2;
            });

            // Filtrar filas que tienen encabezados como valores
            cleanedRows = cleanedRows.filter((row) => {
              return !Object.keys(row).every((key) => row[key] === key);
            });

            // AÃ±adir filas limpias al array unificado
            unifiedData = [...unifiedData, ...cleanedRows];
          });
        });

        // 0. Si viene "importe" en lugar de "importe_pesos", unificamos
        unifiedData = unifiedData.map((item) => {
          if (item.importe !== undefined && item.importe_pesos === undefined) {
            item.importe_pesos = item.importe;
            delete item.importe;
          }
          return item;
        });

        // 1. Omitir importe_pesos negativos
        unifiedData = unifiedData.filter((item) => {
          if (item.importe_pesos !== undefined && item.importe_pesos !== "") {
            let num = parseCurrency(item.importe_pesos);
            if (!isNaN(num)) {
              return num >= 0;
            }
          }
          return true;
        });

        // 2. Formatear importe_pesos con coma decima y punto separador de miles
        unifiedData = unifiedData.map((item) => {
          if (item.importe_pesos !== undefined && item.importe_pesos !== "") {
            let num = parseCurrency(item.importe_pesos);
            if (!isNaN(num)) {
              item.importe_pesos = formatearMontoMoneda(num);
            }
          }
          return item;
        });

        if (sanitizeData) {
          unifiedData = sanitizeData(unifiedData);
        }

        // Guardar el array unificado en el estado
        setProcessedData(unifiedData);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <input
        type="file"
        accept=".xlsx, .xls"
        onChange={handleFileUpload}
        className="file-input"
      />
      {helperText && (
        <p className="text-sm text-gray-600 text-center">
          {helperText}
        </p>
      )}

      <div className="hidden mt-4">
        <h3 className="text-lg font-semibold">Datos del archivo:</h3>
        <pre className="text-sm bg-gray-100 p-2 rounded">
          {JSON.stringify(processedData, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default ImportFromExcel;
